import React, { useState, useEffect, useContext } from "react";
import { cn } from "../../../lib/helpers";
import { SiteContext } from "../../global/site-context";
import { LinkedinShareButton } from "react-share";
import LZString from "lz-string";
import pipelineHeroQuizData from "./pipeline-hero-quiz.json";
import { QuizShareCard } from "./quiz-share-card";
import { ImageBuilder } from "../../global/image-builder";
import { faArrowsToCircle, faChartLine, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WORKFLOW_QUIZ_ID } from "./quiz";
import { QuizLoading } from "./quiz-loading";
import { ATTRIBUTION_PARAM, DEMO_PHRASE, DEMO_URL } from "../snowflake/navigation";

const WAIT_TIME_POLLING = 25000; // after the 30 second timeout
const EXTRA_LOOKUP_WAIT = 12000; // before starting to poll for cached results
const POLLING_INTERVAL = 7000; // poll every 7 seconds
const MAX_POLLING_ATTEMPTS = 11; // how many times to try

// Helper to parse bold text within a line
const parseBoldText = (line) => {
  const parts = [];
  let lastIndex = 0;
  const boldRegex = /\*([^*]+)\*/g;
  let match;

  while ((match = boldRegex.exec(line)) !== null) {
    // Add text before the bold part
    if (match.index > lastIndex) {
      parts.push(line.substring(lastIndex, match.index));
    }
    // Add the bold part
    parts.push(<strong key={`bold-${match.index}`}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last bold part
  if (lastIndex < line.length) {
    parts.push(line.substring(lastIndex));
  }

  return parts.length > 0 ? parts : line;
};

// Helper function to render text with line breaks and bold formatting
const renderTextWithLineBreaks = (text) => {
  if (!text) return null;
  // Ensure we have a string - if it's an object, return null to avoid React error
  if (typeof text !== 'string') {
    console.error('renderTextWithLineBreaks received non-string input:', {
      type: typeof text,
      value: text,
      keys: typeof text === 'object' ? Object.keys(text) : 'N/A'
    });
    return null;
  }

  // Replace double line breaks with single line breaks
  const normalizedText = text.replace(/\n\n+/g, '\n');
  return normalizedText.split('\n').map((line, index, array) => (
    <span key={index}>
      {parseBoldText(line)}
      {index < array.length - 1 && <br />}
    </span>
  ));
};

// Tooltip component
const Tooltip = ({ children, content, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);

  if (!content) return children;

  return (
    <div
      className={cn("relative inline-block", className)}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-80 max-w-sm">
          <div className="bg-black text-white text-sm rounded-lg p-3 shadow-lg border border-gray-800">
            <div className="text-light-80">{content}</div>
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
};

// Quiz Result Card Component
const QuizResultCard = ({
  quizResult,
  topArchetype,
  contentData,
  contentLoading,
  contentError,
  isPollingCache,
  getEmailDomain,
  showEmergencyButton,
  onEmergencyLookup
}) => {
  const site = useContext(SiteContext);
  // Generate share URL with encoded data
  const generateShareUrl = () => {
    try {

      const shareData = {
        persona: quizResult.dept || quizResult.persona,
        dept: quizResult.dept,
        seniority: quizResult.role || quizResult.seniority,
        archetype: resolvedArchetype?.name || topArchetype.name,
        cta: quizResult.cta,
        score: topArchetype.score,
        // Include all available org info
        org: {
          facts: {
            name: quizResult.org?.facts?.name || null,
          }
        },
        email: quizResult.email,
        // Only include the share text from content
        content: { share: (quizResult.content?.share || contentData?.share || null) }
      };

      // Use LZString compression for much shorter URLs
      const jsonString = JSON.stringify(shareData);
      const compressedData = LZString.compressToEncodedURIComponent(jsonString);

      const heroId = resolvedArchetype?.id || 'oracle';
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

      // Format: /hero/oracle/?share=555#quiz
      const shareUrl = `${baseUrl}/hero/${heroId}/?share=${compressedData}`;

      return shareUrl;
    } catch (error) {
      console.error('Error generating share URL:', error);
      return `${typeof window !== 'undefined' ? window.location.origin : ''}/hero/`;
    }
  };

  // Resolve archetype from quiz results
  const resolveArchetype = (name) => {
    if (!pipelineHeroQuizData?.archetypes) return null;
    const n = (name || '').toLowerCase();
    const { archetypes } = pipelineHeroQuizData;

    // First try direct key match (most reliable for quiz results)
    if (archetypes[n]) {
      return archetypes[n];
    }

    // Then try matching by display name or partial matches
    return Object.values(archetypes).find(a => {
      if (!a) return false;
      return (
        (a.name && a.name.toLowerCase().includes(n)) ||
        n.includes('oracle') && a.name?.toLowerCase().includes('oracle') ||
        n.includes('clarity') && a.name?.toLowerCase().includes('oracle') ||
        n.includes('striker') && a.name?.toLowerCase().includes('striker') ||
        n.includes('timing') && a.name?.toLowerCase().includes('striker') ||
        n.includes('flashpoint') && a.name?.toLowerCase().includes('striker') ||
        n.includes('resonator') && a.name?.toLowerCase().includes('resonator')
      );
    });
  };

  const resolvedArchetype = resolveArchetype(topArchetype.name);

  // Get hero background image based on resolved archetype
  const getHeroBackgroundImage = () => {
    return resolvedArchetype?.backgroundImage || 'oracle_horizontal.png';
  };

  // Get hero power icon based on resolved archetype
  const getHeroPowerIcon = () => {
    return resolvedArchetype?.powerIcon || 'power-icon-sight.svg';
  };

  return (
    <>
      <div
        className="max-w-8xl mx-auto bg-gray-1000 border-dark-30 rounded-2xl shadow-lg"
        style={{ borderWidth: "5px" }}
      >
        {/* TOP SECTION - Graphic Header */}
        <div
          style={{
            minHeight: '200px',
            backgroundColor: resolvedArchetype?.borderColor
          }}
          className="relative rounded-t-xl mb-12 relative font-superhero"
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <ImageBuilder
              src={`/img/quiz/${getHeroBackgroundImage()}`}
              alt={`${resolvedArchetype?.name || topArchetype.name} hero background`}
              className="w-full h-full object-cover object-center rounded-t-xl"
            />
          </div>
          {/* Hero info */}
          <div className="absolute inset-0 z-10 flex items-center">
            <div className="text-white uppercase italic ml-4 md:ml-8">
              <h1 className="text-5xl lg:text-7xl mb-2 font-semibold drop-shadow-lg">
                {resolvedArchetype?.name || topArchetype.name}
              </h1>
              <div className="text-xl lg:text-2xl drop-shadow">
                {resolvedArchetype?.subtitle}
              </div>
            </div>
          </div>

          {/* Bottom center - Power icon */}
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 z-20">
            <div
              className="p-1 rounded-2xl flex items-center justify-center shadow-lg border-4 bg-black"
              style={{ borderColor: resolvedArchetype?.borderColor }}
            >
              <ImageBuilder
                src={`/img/quiz/${getHeroPowerIcon()}`}
                alt={`${resolvedArchetype?.name || topArchetype.name} power icon`}
                className="w-12 h-12"
              />
            </div>
          </div>
        </div>

        {/* MAIN CONTENT - Two Column Layout */}
        <div
          className="grid grid-cols-1 lg:grid-cols-3/5-2/5 gap-8 lg:gap-16 items-start p-4 md:p-6 pb-12 font-openSans"
        >
          {/* LEFT SIDE - Quiz Results */}
          <div className="space-y-10" id="results">
            {/* Archetype Description */}
            <div>
              <p className="text-light-80">
                {renderTextWithLineBreaks((quizResult.content || contentData)?.archetypeResults ||
                  `You are ${topArchetype.name}, someone who excels at driving results through focused action and strategic thinking.`)}
              </p>
            </div>

            {/* Company Info */}
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-light-50 text-sm mb-0.5">Department</p>
                  <div className="text-white font-medium">{quizResult.dept || quizResult.persona || 'Not specified'}</div>
                </div>
                <div>
                  <p className="text-light-50 text-sm mb-0.5">Role</p>
                  <div className="text-white font-medium">{quizResult.role || quizResult.seniority || 'Not specified'}</div>
                </div>

                {quizResult.org?.facts ? (
                  <>
                    {/* Render all org.facts fields with tooltips for org.background values */}
                    {(() => {
                      if (!quizResult.org.facts) return null;
                      // Specify the desired order of keys
                      const orderedKeys = [
                        'employees',
                        'traffic',
                        'teamSize',
                        'social',
                        'funding',
                        'offering',
                        'industry',
                        'name',
                        'customers',
                        'news',
                      ];
                      // Remove duplicates and only show keys that exist in facts or background
                      const shownKeys = orderedKeys.filter(key => quizResult.org.facts[key] !== undefined || quizResult.org.background?.[key]);
                      return shownKeys.map(key => {
                        let value = quizResult.org.facts[key];
                        const prettyKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                        // For company name, use background.company instead of background.name
                        const tooltipContent = key === 'name'
                          ? quizResult.org.background?.company
                          : quizResult.org.background?.[key];
                        // Customers and News: always show as 'View details' link with tooltip
                        if (["customers", "news"].includes(key) && tooltipContent) {
                          return (
                            <Tooltip key={key} content={tooltipContent}>
                              <div className="cursor-help">
                                <p className="text-light-50 text-sm mb-0.5">{prettyKey}</p>
                                <p className="text-link text-sm hover:white">View details</p>
                              </div>
                            </Tooltip>
                          );
                        }
                        if (key === 'name') {
                          return (
                            <Tooltip key={key} content={tooltipContent}>
                              <div className="cursor-help">
                                <p className="text-light-50 text-sm mb-0.5">Company</p>
                                <p className="text-link text-sm hover:white">View details</p>
                              </div>
                            </Tooltip>
                          )
                        }
                        // For productLed and openSource, show check/X
                        if (["productLed", "openSource"].includes(key)) {
                          let isTrue = value === "true" || value === true || value === "Yes";
                          value = isTrue ? "✅" : "❌";
                        }
                        // For other fields, show value and tooltip if present
                        if (value !== undefined) {
                          return tooltipContent ? (
                            <Tooltip key={key} content={tooltipContent}>
                              <div className="cursor-help">
                                <p className="text-light-50 text-sm mb-0.5">{prettyKey}</p>
                                <div className="text-white leading-snug text-sm">{value}</div>
                              </div>
                            </Tooltip>
                          ) : (
                            <div key={key}>
                              <p className="text-light-50 text-sm mb-0.5">{prettyKey}</p>
                              <div className="text-white leading-snug text-sm">{value}</div>
                            </div>
                          );
                        }
                        // If only background exists, show 'View details' link
                        if (tooltipContent) {
                          return (
                            <Tooltip key={key} content={tooltipContent}>
                              <div className="cursor-help">
                                <p className="text-light-50 text-sm mb-0.5">{prettyKey}</p>
                                <p className="text-link text-sm hover:white">View details</p>
                              </div>
                            </Tooltip>
                          );
                        }
                        return null;
                      });
                    })()}
                  </>
                ) : (
                  <div className="col-span-2">
                    <p className="text-light-60 mb-1 text-sm">Domain</p>
                    <div className="text-white font-medium">{getEmailDomain() || 'Company information loading...'}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Content Blocks */}
            <div>
              <h3 className="font-superhero uppercase italic text-white text-2xl mb-4">
                Personalized insights
              </h3>
              {(quizResult.content || contentData) && (
                <div className="space-y-10">
                  <div>
                    <h4 className="text-lg text-green-emerald mb-2 text-yellow-300">
                      <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                      Strength & growth areas
                    </h4>
                    <p className="text-light-60">{renderTextWithLineBreaks((quizResult.content || contentData)?.strengthGrowth)}</p>
                  </div>
                  <div>
                    <h4 className="text-lg mb-2 text-red">
                      <FontAwesomeIcon icon={faCircleExclamation} className="mr-2" />
                      Pitfalls to avoid
                    </h4>
                    <p className="text-light-60">{renderTextWithLineBreaks((quizResult.content || contentData)?.pitfalls)}</p>
                  </div>
                  <div>
                    <h4 className="text-lg mb-2 text-green-emerald">
                      <FontAwesomeIcon icon={faArrowsToCircle} className="mr-2" />
                      Where to focus next
                    </h4>
                    <p className="text-light-60">{renderTextWithLineBreaks((quizResult.content || contentData)?.nextFocus)}</p>
                  </div>
                </div>
              )}
            </div>
            <p className="text-light-40 text-sm italic">
              Powered by AI magic from Claude, Gemini, and ChatGPT.
            </p>
          </div>

          {/* RIGHT SIDE - Share Section - Only show when content is loaded */}
          {(quizResult.content || contentData) && !contentLoading && !contentError && (
            <div className="space-y-6 lg:sticky lg:top-4 lg:self-start" id="share">
              {/* Share card - now uses official QuizShareCard component */}
              <QuizShareCard data={{
                archetype: topArchetype.name,
                resolvedArchetype: resolvedArchetype ? {
                  name: resolvedArchetype.name,
                  subtitle: resolvedArchetype.subtitle,
                  borderColor: resolvedArchetype.borderColor,
                  squadImage: resolvedArchetype.squadImage,
                  powerIcon: resolvedArchetype.powerIcon,
                } : null,
                org: {
                  facts: {
                    name: quizResult.org?.facts?.name || null,
                  }
                },
                dept: quizResult.dept || quizResult.persona,
                persona: quizResult.persona,
                cta: quizResult.cta,
                content: { share: (quizResult.content?.share || contentData?.share || null) },
              }} />

              {/* Share section */}
              <div className="space-y-6 mx-4 lg:mx-8">
                <div className="text-center">
                  <LinkedinShareButton
                    url={generateShareUrl()}
                    title="I unlocked my secret superhero identity!"
                    summary={`I'm a ${resolvedArchetype?.name || topArchetype.name} who excels at driving results through focused action and strategic thinking. Take the quiz to discover your superpowers!`}
                    source="Common Room"
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#16A574';
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#1BC88C';
                      e.target.style.transform = 'scale(1)';
                    }}
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        site.metrics.logClick("hero-quiz", "share-linkedin");
                      }
                    }}
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#1BC88C',
                      fontWeight: '600',
                      color: 'black',
                      borderRadius: '8px',
                      paddingLeft: '24px',
                      paddingRight: '24px',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      transform: 'scale(1)',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      boxShadow: '1px 1px 34px 4px rgba(34, 216, 153, 0.25)',
                    }}
                  >
                    Share on LinkedIn
                  </LinkedinShareButton>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={generateShareUrl()}
                    readOnly
                    className="w-full px-3 py-2 bg-dark-50 border border-light-50 rounded-lg text-light-40 hover:text-light-60 transition-color duration-300 text-sm focus:outline-none focus:ring-0 truncate"
                    placeholder="Generating share URL..."
                  />
                  <button
                    onClick={(event) => {
                      const url = generateShareUrl();
                      navigator.clipboard.writeText(url).then(() => {
                        // Simple feedback - you could enhance this with a toast
                        const button = event.target;
                        button.classList.add('text-white');
                        setTimeout(() => {
                          button.classList.remove('text-black');
                        }, 2000);
                      }).catch(err => {
                        console.error('Failed to copy URL:', err);
                      });
                    }}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-green-emerald hover:bg-blue-600 text-black p-1 rounded-md transition-all duration-200 hover:scale-105"
                    title="Copy to clipboard"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          <div>
            {contentError && (
              <div className="text-center py-8">
                <div className="text-red-400 mb-4">
                  <svg className="w-12 h-12 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-light-60 mb-6">{contentError}</p>
                {showEmergencyButton && (
                  <div className="flex flex-col items-center gap-3">
                    <button
                      onClick={onEmergencyLookup}
                      className="px-8 py-3 bg-superhero-link text-black font-semibold rounded-lg hover:bg-blue-400 transition-all duration-300 active:scale-95"
                      style={{
                        boxShadow: '0 0 20px 2px rgba(34, 216, 153, 0.3)'
                      }}
                    >
                      Emergency Final Squad Lookup
                    </button>
                    <p className="text-light-50 text-sm">
                      Click for manual super hero quiz results retrieval.
                    </p>
                  </div>
                )}
              </div>
            )}
            {(contentLoading || isPollingCache) && (
              <>
                <QuizLoading skipWrapper={true} />
                <div className="text-center pb-12 text-light-50 font-openSans">
                  Ready in about a minute!
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {/* CTA Section */}
      <div className="mt-12 text-center max-w-4xl mx-auto">
        <p className="text-light-80 text-2xl mb-6 leading-snug">
          {quizResult.cta || "Unlock potential with an AI-powered customer intelligence platform"}
        </p>
        <a
          href={`${DEMO_URL}?${ATTRIBUTION_PARAM}=hero-quiz-results`}
          className="text-xl inline-block bg-superhero-link font-semibold text-black rounded-lg hover:text-dark-80 px-6 py-3 transform scale-100 hover:scale-105 transition-all duration-300"
          style={{
            boxShadow: '1px 1px 34px 4px rgba(34, 216, 153, 0.25)',
          }}
        >
          {DEMO_PHRASE}
        </a>
      </div>
    </>
  );
};

export const QuizResults = ({ quizResult, sessionHash, onReset }) => {
  const [sectionVisibility, setSectionVisibility] = useState({
    archetype: false,
    profile: false,
    domain: false,
    content: false
  });
  const [contentData, setContentData] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState(null);
  const [enrichedQuizResult, setEnrichedQuizResult] = useState(null);
  const [apiAttempted, setApiAttempted] = useState(false);
  const [isPollingCache, setIsPollingCache] = useState(false);
  const [showEmergencyButton, setShowEmergencyButton] = useState(false);
  const [isRoomie, setIsRoomie] = useState(false);
  const site = useContext(SiteContext);

  // Check for roomie status from URL param or storage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const hasRoomieParam = urlParams.has('roomie');

      if (hasRoomieParam) {
        setIsRoomie(true);
        console.log('Roomie from URL:', urlParams.get('roomie'));
        return;
      }

      // Fallback to checking storage
      if (site?.metrics?.analytics?.storage) {
        const storageRoomie = site.metrics.analytics.storage.getItem("roomie");
        setIsRoomie(!!storageRoomie);
        console.log('Roomie from storage:', storageRoomie, 'isRoomie:', !!storageRoomie);
      }
    }
  }, [site]);

  // Use enriched result if available, otherwise use original
  const currentQuizResult = enrichedQuizResult || quizResult;

  // Get the top archetype from matches or fallback to direct archetype property
  const getTopArchetype = () => {
    if (currentQuizResult?.matches && currentQuizResult.matches.length > 0) {
      // Find the match with the highest score
      const topMatch = currentQuizResult.matches.reduce((prev, current) => {
        const prevScore = parseInt(prev.score.split('/')[0]);
        const currentScore = parseInt(current.score.split('/')[0]);
        return currentScore > prevScore ? current : prev;
      });
      return topMatch;
    }
    // Fallback to direct archetype property (for example data)
    return {
      name: currentQuizResult?.archetype || 'Unknown',
      score: currentQuizResult?.matches ? 'N/A' : '7/9'
    };
  };

  const topArchetype = getTopArchetype();

  // Extract email domain from quiz result
  const getEmailDomain = () => {
    if (currentQuizResult?.email) {
      return currentQuizResult.email.split('@')[1];
    }
    return null;
  };

  // Fetch content from AirOps API with timeout and retry logic
  const fetchContentData = async () => {
    if (!quizResult?.email) return;

    setContentLoading(true);
    setContentError(null);
    setApiAttempted(true);
    setIsPollingCache(false);

    // Calculate dynamic wait time - add 10s if company data is missing (needs lookup)
    const dynamicWaitTime = (!quizResult.companyData || quizResult.companyData === '')
      ? WAIT_TIME_POLLING + EXTRA_LOOKUP_WAIT
      : WAIT_TIME_POLLING;

    try {
      // Create a timeout promise that resolves after 30 seconds
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 30000);
      });

      // Create the fetch promise
      const fetchPromise = fetch("/.netlify/functions/airops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          inputs: {
            dept: quizResult.dept || quizResult.persona,
            role: quizResult.role || quizResult.seniority,
            email: quizResult.email,
            matches: [{ name: topArchetype.name, score: topArchetype.score }],
            quiz: JSON.stringify(quizResult.quiz || []),
            company: quizResult.companyData || '',
            // For cached lookups
            hash: sessionHash,
            workflowId: WORKFLOW_QUIZ_ID,
            token: site.buildToken
          }
        })
      });

      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      if (response.ok) {
        const data = await response.json();
        console.log("Quiz Response:", data);
        console.log("Quiz content generated successfully:", data.result);
        processSuccessfulResponse(data.result);
      } else {
        // Handle server timeouts (504, 502) by starting polling
        if (response.status === 504 || response.status === 502) {
          console.log(`Server timeout detected, will start polling for cached results in ${dynamicWaitTime / 1000} seconds`);

          setTimeout(() => {
            startPollingForCachedResults();
          }, dynamicWaitTime);
        } else {
          // Try to get error data for other non-OK responses
          try {
            const data = await response.json();
            console.error('API error:', response.statusText, data);
          } catch (jsonError) {
            console.error('API error (no JSON):', response.statusText);
          }
          setContentError('Failed to generate personalized content.');
          setShowEmergencyButton(true);
          setContentLoading(false);
          site.metrics.logEvent({
            category: "error",
            action: "hero-quiz",
            label: "results-unexpected-status",
          });
        }
      }
    } catch (error) {
      console.error('Error fetching quiz content from API:', error);

      // Handle both our custom timeout and network errors
      if (error.message === 'Request timeout') {
        console.log('Request timed out, will start polling for cached results in', dynamicWaitTime / 1000, 'seconds');

        setTimeout(() => {
          startPollingForCachedResults();
        }, dynamicWaitTime);
      } else {
        setContentError('Connection error. Please check your internet connection.');
        setShowEmergencyButton(true);
        setContentLoading(false);
        site.metrics.logEvent({
          category: "error",
          action: "hero-quiz",
          label: "results-connection",
        });
      }
    }
  };

  // Process successful API response (extracted for reuse)
  const processSuccessfulResponse = (result) => {
    // Handle different response types
    let parsedResult = result;

    if (typeof result === 'string') {
      try {
        parsedResult = JSON.parse(result);
        console.log("Parsed cached result:", parsedResult);
      } catch (parseError) {
        console.error("Failed to parse JSON result:", parseError);
        // If parsing fails, treat it as raw text content
        parsedResult = { content: { archetype: result } };
      }
    } else if (typeof result === 'object' && result !== null) {
      // Result is already an object, use it directly
      parsedResult = result;
      console.log("Using object result directly:", parsedResult);
    } else {
      // Fallback for unexpected result types
      console.warn("Unexpected result type:", typeof result, result);
      parsedResult = { content: { archetype: String(result) } };
    }

    // Create enriched quiz result by merging AirOps data with original quiz result
    const enrichedResult = {
      ...quizResult,
      // Add organization data if available
      ...(parsedResult?.org && { org: parsedResult.org }),
      // Add content data if available
      ...(parsedResult?.content && { content: parsedResult.content }),
      // Add CTA if available
      ...(parsedResult?.cta && { cta: parsedResult.cta }),
      // Add archetype if available
      ...(parsedResult?.archetype && { archetype: parsedResult.archetype })
    };

    setEnrichedQuizResult(enrichedResult);
    setContentData(parsedResult?.content || null);
    setContentLoading(false);
    setIsPollingCache(false);

    site.metrics.logEvent({
      category: "passive",
      action: "hero-quiz",
      label: "results-success",
    });
  };

  // Poll for cached results every 5 seconds
  const startPollingForCachedResults = async () => {
    setIsPollingCache(true);

    const pollForResults = async () => {
      try {
        console.log('Polling for cached results...');

        const response = await fetch("/.netlify/functions/airops", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            inputs: {
              email: quizResult.email,
              hash: sessionHash,
              workflowId: WORKFLOW_QUIZ_ID,
              token: site.buildToken
            }
          })
        });

        const data = await response.json();

        if (response.ok && data.result) {
          console.log("Found cached results:", data.result);
          processSuccessfulResponse(data.result);
          return true; // Success, stop polling
        } else {
          console.log('No cached results yet, will retry in 5 seconds...');
          return false; // Continue polling
        }
      } catch (error) {
        console.error('Error polling for cached results:', error);
        return false; // Continue polling
      }
    };

    // Start polling with attempt counter
    let pollAttempts = 0;

    const poll = async () => {
      pollAttempts++;
      console.log(`Polling attempt ${pollAttempts}/${MAX_POLLING_ATTEMPTS}`);

      const success = await pollForResults();
      if (!success && pollAttempts < MAX_POLLING_ATTEMPTS) {
        // Continue polling every 7 seconds for up to 6 attempts
        setTimeout(poll, POLLING_INTERVAL);
      } else if (!success && pollAttempts >= MAX_POLLING_ATTEMPTS) {
        // Stop polling after max attempts
        console.log(`Polling limit reached after ${MAX_POLLING_ATTEMPTS * POLLING_INTERVAL / 1000} seconds`);
        setContentError('Unable to retrieve your personalized results.');
        setContentLoading(false);
        setIsPollingCache(false);
        setShowEmergencyButton(true);
        site.metrics.logEvent({
          category: "error",
          action: "hero-quiz",
          label: "polling-timeout",
        });
      }
    };

    poll();
  };

  // Manual emergency lookup function
  const handleEmergencyLookup = async () => {
    setShowEmergencyButton(false);
    setContentLoading(true);
    setContentError(null);
    site.metrics.logEvent({
      category: "click",
      action: "hero-quiz",
      label: "emergency-lookup",
    });

    try {
      console.log('Manual emergency lookup triggered...');

      const response = await fetch("/.netlify/functions/airops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          inputs: {
            email: quizResult.email,
            hash: sessionHash,
            workflowId: WORKFLOW_QUIZ_ID,
            token: site.buildToken
          }
        })
      });

      const data = await response.json();

      if (response.ok && data.result) {
        console.log("Manual lookup successful:", data.result);
        processSuccessfulResponse(data.result);
        site.metrics.logClick("hero-quiz", "emergency-lookup-success");
      } else {
        console.log('Manual lookup failed, no results found');
        setContentError('Still unable to retrieve results. Please try again.');
        setContentLoading(false);
        setShowEmergencyButton(true);
        site.metrics.logClick("hero-quiz", "emergency-lookup-fail");
      }
    } catch (error) {
      console.error('Error in manual lookup:', error);
      setContentError('Connection error during manual lookup.');
      setContentLoading(false);
      setShowEmergencyButton(true);
    }
  };

  useEffect(() => {
    if (currentQuizResult) {
      // Show all sections immediately
      setSectionVisibility({
        archetype: true,
        profile: true,
        domain: true,
        content: true
      });

      // Start fetching content immediately if it doesn't exist
      if (!currentQuizResult.content && !apiAttempted) {
        fetchContentData();
      }
    }
  }, [quizResult, apiAttempted]);

  return (
    <div>
      {currentQuizResult ? (
        <>
          <QuizResultCard
            quizResult={currentQuizResult}
            topArchetype={topArchetype}
            contentData={contentData}
            contentLoading={contentLoading}
            contentError={contentError}
            isPollingCache={isPollingCache}
            getEmailDomain={getEmailDomain}
            showEmergencyButton={showEmergencyButton}
            onEmergencyLookup={handleEmergencyLookup}
          />
        </>
      ) : (
        <div className="max-w-4xl mx-auto bg-dark-30 rounded-xl p-6 pb-12 border border-dark-60">
          <div className="text-center py-8">
            <p className="text-lg mb-4 text-light-80">Thank you for completing the quiz!</p>
            <p className="text-sm text-light-70">
              Processing your results...
            </p>
          </div>
        </div>
      )}
    </div>
  )
};
